import classNames from 'classnames';
import { camelCase } from 'lodash';
import type { FC } from 'react';
import { useCallback, useContext, useEffect, useMemo } from 'react';
import { useRef } from 'react';
import React from 'react';

import ConfigRentContext from '../../../service/context/config-rent';
import useAppStoreRent from '../../../service/store/appRent';
import useOrderStoreRent from '../../../service/store/orderRent';
import { DEFAULT_DELIVERY_COUNTRY } from '../../../utils/constants';
import { getWorkdayOptions } from '../../../utils/date';
import { formatCurrency } from '../../../utils/format';
import { OPTION_DELIVERY, OPTION_PICKUP } from '../constants';

import Address from './../../../components/Address';
import AddressToggle from './../../../components/AddressToggle';
import ContentNavigation from './../../../components/ContentNavigation';
import Button from './../../../components/ui/Button';
import Input from './../../../components/ui/Input';
import './../../../components/ui/Input.scss';
import { scrollToError, scrollToRef } from './../../../utils/scroll';
import { validateOrderRent } from './../../../utils/validate';

interface IStepCustomerDataProps {
  placeOrder?: () => Promise<void>;
}

const StepCustomerData: FC<IStepCustomerDataProps> = ({
  placeOrder
}) => {
  const {
    delivery_options,
    pickup_options,
    shipping_holidays,
    date_options_count = 2,
    delivery_countries_mapping: countryMapping
  } = useContext(ConfigRentContext) || {};
  const errorRef = useRef<null | HTMLDivElement>(null);
  const {
    showErrors,
    setShowErrors,
    setStep,
    step
  } = useAppStoreRent(state => state);
  const order = useOrderStoreRent(state => state.order);
  const {
    customer_number,
    reference_number,
    coupon,
    address,
    diffDeliveryAddress,
    deliveryAddress,
    diffInvoiceRecipient,
    invoiceRecipient,
    delivery_date,
    pickup_date,
    delivery_type,
    delivery_option,
    pickup_option
  } = order || {};
  const setOrderValue = useOrderStoreRent(state => state.setValue);
  const validOrder = validateOrderRent(order);

  // Datum-Optionen für Versand (Cutoff: 15:00 Uhr, Feiertage: Hamburg)
  const shippingDateOptions = useMemo(
    () => getWorkdayOptions(date_options_count, 15, shipping_holidays || []),
    [ date_options_count, shipping_holidays ]
  );

  // Datum-Optionen für Abholung (Cutoff: je nach Standort und Wochentag)
  const pickupDateOptions = useMemo(
    () => getWorkdayOptions(
      date_options_count,
      pickup_option?.cutoff_hours || 17,
      pickup_option?.holidays || []
    ),
    [ date_options_count, pickup_option?.cutoff_hours, pickup_option?.holidays ]
  );

  // Maßgebliches Lieferland: das der abweichenden Lieferadresse, sonst das der
  // Hauptadresse. Fallback auf das Standardland, solange noch keins gewählt ist.
  const selectedDeliveryCountry = (diffDeliveryAddress
    ? deliveryAddress?.country
    : address?.country) || DEFAULT_DELIVERY_COUNTRY;
  const selectedCountryCode = countryMapping?.[selectedDeliveryCountry];

  // Nur Versandoptionen zum gewählten Lieferland anzeigen (country_code-Filter).
  const visibleDeliveryOptions = useMemo(
    () => (delivery_options || []).filter(option => option.country_code === selectedCountryCode),
    [ delivery_options, selectedCountryCode ]
  );

  const isDelivery = delivery_type === OPTION_DELIVERY;
  const isPickup = delivery_type === OPTION_PICKUP;

  // Beim Wechsel des Lieferlands die Versandauswahl zurücksetzen, da die zuvor
  // gewählte Option zu einem anderen Land gehören kann. Es wird auf die erste
  // Option des neuen Lands gesetzt (Standardversand).
  useEffect(() => {
    if (!isDelivery) {
      return;
    }

    const currentStillValid = delivery_option
      && visibleDeliveryOptions.some(option => option.id === delivery_option.id);

    if (!currentStillValid) {
      setOrderValue(visibleDeliveryOptions[0], 'delivery_option');
    }
  }, [ isDelivery, visibleDeliveryOptions, delivery_option, setOrderValue ]);

  const handleInputChange = useCallback((
    event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ): void => {
    setOrderValue(event.target.value, event.target.name);
  }, [ setOrderValue ]);

  const handleToggleAddress = (propertyToSet: string, visible: boolean): void => {
    setOrderValue(visible, camelCase(`diff ${propertyToSet}`));
  };

  const handleCtaClick = (): void => {
    if (validOrder && placeOrder) {
      void placeOrder();
      scrollToRef();
    } else {
      setShowErrors(true);
      scrollToError(errorRef);
    }
  };

  return (
    <>
      <ContentNavigation
        step={step}
        setStep={setStep}
        showBackButton
      >
        <Button
          className={classNames('btn-dark', {
            'btn-disabled': !validOrder
          })}
          onClick={handleCtaClick}
          hasRightIcon
          // disabled={!validOrder}
        >
          Jetzt Miete beauftragen
        </Button>
      </ContentNavigation>
      <div className="box">
        <div className="box-content">
          <div ref={errorRef}>
            {showErrors && !validOrder && (
              <div className="error-box">
                Bitte fülle alle Pflichtfelder aus.
              </div>
            )}
          </div>
          <div>
            <div className="styled-input">
              <label>Versand</label>
              <div className="buttons-wrap">
                <button
                  className={classNames({ active: isDelivery })}
                  onClick={() => setOrderValue(OPTION_DELIVERY, 'delivery_type')}
                >
                  Senden Sie mir alles zu
                </button>
                <button
                  className={classNames({ active: isPickup })}
                  onClick={() => setOrderValue(OPTION_PICKUP, 'delivery_type')}
                >
                  Ich hole alles bei ToolService ab
                </button>
              </div>
            </div>
          </div>
          <div className="rent-form-delivery-pickup-options">
            {isDelivery && (
              <>
                <div className={classNames('styled-input', {
                  'has-error': showErrors && !delivery_option
                })}>
                  <label htmlFor="delivery_option">Versandart *</label>
                  <select
                    name="delivery_option"
                    value={delivery_option?.id}
                    onChange={event => {
                      const selectedId = event.target.value;
                      const selected = visibleDeliveryOptions.find(option => option.id === selectedId);

                      if (selected) {
                        setOrderValue(selected, 'delivery_option');
                      }
                    }}
                  >
                    {visibleDeliveryOptions.map(option => (
                      <option
                        key={option.id}
                        value={option.id}
                      >
                        {option.name} ({formatCurrency(option.cost)})
                      </option>
                    ))}
                  </select>
                </div>
                <div className={classNames('styled-input', {
                  'has-error': showErrors && !delivery_date
                })}>
                  <label>
                    Bitte spätestens versenden am *
                  </label>
                  <div className="date-options">
                    {shippingDateOptions.map(option => (
                      <label
                        key={option.dateStr}
                        className={classNames('date-option', {
                          'is-checked': delivery_date === option.dateStr
                        })}
                      >
                        <input
                          type="radio"
                          name="delivery_date_option"
                          value={option.dateStr}
                          checked={delivery_date === option.dateStr}
                          onChange={() => setOrderValue(option.dateStr, 'delivery_date')}
                        />
                        <span className="radio-box" />
                        <span>{option.label}</span>
                      </label>
                    ))}
                  </div>
                  <p className="input-info">
                    Versand am gleichen Tag bei Bestellung vor 15:00 Uhr möglich.
                  </p>
                </div>
              </>
            )}
            {isPickup && (
              <>
                <div className={classNames('styled-input', {
                  'has-error': showErrors && !pickup_option
                })}>
                  <label htmlFor="pickup_option">Abholort *</label>
                  <select
                    name="pickup_option"
                    value={pickup_option?.id}
                    onChange={event => {
                      const selectedId = event.target.value;
                      const selected = pickup_options?.find(option => option.id === selectedId);

                      if (selected) {
                        setOrderValue(selected, 'pickup_option');
                      }
                    }}
                  >
                    {pickup_options?.map(option => (
                      <option
                        key={option.id}
                        value={option.id}
                      >
                        {option.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className={classNames('styled-input', {
                  'has-error': showErrors && !pickup_date
                })}>
                  <label>
                    Abholdatum *
                  </label>
                  <div className="date-options">
                    {pickupDateOptions.map(option => (
                      <label
                        key={option.dateStr}
                        className={classNames('date-option', {
                          'is-checked': pickup_date === option.dateStr
                        })}
                      >
                        <input
                          type="radio"
                          name="pickup_date_option"
                          value={option.dateStr}
                          checked={pickup_date === option.dateStr}
                          onChange={() => setOrderValue(option.dateStr, 'pickup_date')}
                        />
                        <span className="radio-box" />
                        <span>{option.label}</span>
                      </label>
                    ))}
                  </div>
                  {pickup_option?.opening_hours && (
                    <p className="input-info">
                      Öffnungszeiten: {pickup_option.opening_hours}
                    </p>
                  )}
                </div>
              </>
            )}
          </div>
          <Address
            showError={showErrors}
            propertyKey="address"
            address={address}
            onChange={handleInputChange}
            customerNumber={customer_number}
            showCustomerNumber
            referenceNumber={reference_number}
            showCountrySelect
            showReferenceNumber
          />
          <Input
            label="Gutscheincode"
            name="coupon"
            value={coupon || ''}
            onChange={handleInputChange}
            placeholder="Gutscheincode eingeben"
          />
          {isDelivery && (
            <AddressToggle
              showError={showErrors}
              title="Abweichende Lieferadresse?"
              propertyKey="deliveryAddress"
              address={deliveryAddress}
              showAddress={diffDeliveryAddress}
              onChange={handleInputChange}
              toggleFunction={handleToggleAddress}
              showCountrySelect
            />
          )}
          <AddressToggle
            showError={showErrors}
            title="Abweichender Rechnungsempfänger?"
            propertyKey="invoiceRecipient"
            address={invoiceRecipient}
            showAddress={diffInvoiceRecipient}
            onChange={handleInputChange}
            toggleFunction={handleToggleAddress}
            emailLabel="E-Mail Rechnungsempfänger"
            hideName
            showCountrySelect
          />
        </div>
      </div>
    </>
  );
};

export default StepCustomerData;
